import Sentry from "@similarweb/sentry-logger";

import type { IIntentDistributionChartData } from "app/pages/keyword-research/common-components/intent-distribution-chart/intentDistributionChartTypes";

import { getDominantIntent } from "../intent/intentUtils";
import type { IIntentVolumeDistribution } from "app/common/keyword-research/keywords-overview/serpCompositionTypes";

import { useOverviewStatsData } from "./useOverviewStatsData";

type IntentData = IIntentDistributionChartData | string;

interface IUseIntentData {
    intentData: IntentData | undefined;
    isLoading: boolean;
    error: unknown;
}

export const useIntentData = (isKeywordGroup: boolean): IUseIntentData => {
    const { data, isLoading, error } = useOverviewStatsData();

    try {
        let intentData: IntentData | undefined;
        const intentResponseData = data?.IntentVolumeDistribution;
        const isDataReady = !isLoading && !error && !!intentResponseData && Object.keys(intentResponseData).length > 0;

        if (isDataReady) {
            const lowerCaseIntentData = Object.keys(intentResponseData).reduce(
                (acc, key: keyof IIntentVolumeDistribution) => {
                    acc[key.toLowerCase()] = intentResponseData[key];
                    return acc;
                },
                {} as IIntentDistributionChartData,
            );
            intentData = !isKeywordGroup ? getDominantIntent(lowerCaseIntentData) : lowerCaseIntentData;
        }

        return {
            intentData,
            isLoading,
            error,
        };
    } catch (err: unknown) {
        Sentry.errorTracker({
            message: "Error transforming keywords overview intent data",
            e: err,
            level: "error",
        });
        return {
            intentData: undefined,
            isLoading: false,
            error: err,
        };
    }
};
